import React, { FC, useRef, useState } from 'react';
import { CreateLoyaltyMemberInput, useButton } from '~Hooks';
import useI18n from '~Translations';
import { Box, Input } from '~Styles';
import { Body, CancelButton, ConfirmButton, ErrorNote, Footer } from '../styles';

/** Enough to accept a Swedish personnummer with or without century digits. */
const PERSONAL_ID_MIN_DIGITS = 10;
const PERSONAL_ID_MAX_DIGITS = 12;

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

interface Props {
  /** Enrols the member; the parent moves on when this resolves. Throws to stay. */
  onCreate: (input: CreateLoyaltyMemberInput) => Promise<void>;
  onBack: () => void;
}

/** Enrolment form. Name and email are what the backend requires; the
 * personnummer is optional but makes the member findable by it later. */
const CreateStep: FC<Props> = ({ onCreate, onBack }) => {
  const { i18n } = useI18n();

  const nameRef = useRef<HTMLInputElement>(null);
  const emailRef = useRef<HTMLInputElement>(null);
  const personalIdRef = useRef<HTMLInputElement>(null);

  const [isCreating, setIsCreating] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const create = async () => {
    if (isCreating) {
      return;
    }

    const name = nameRef.current?.value.trim() ?? '';
    const email = emailRef.current?.value.trim() ?? '';
    const personalId = personalIdRef.current?.value.replace(/\D/g, '') ?? '';

    const isValid =
      name.length > 1 &&
      EMAIL_PATTERN.test(email) &&
      // Optional, but nonsense inputs are stopped here rather than server-side.
      (personalId.length === 0 || personalId.length >= PERSONAL_ID_MIN_DIGITS);

    if (!isValid) {
      setError(i18n('Loyalty.CreateFailed'));

      return;
    }

    setError(null);
    setIsCreating(true);

    try {
      await onCreate({
        name,
        email,
        personalIdentityNumber: personalId || undefined,
      });
    } catch (err) {
      const reason = err instanceof Error ? err.message : String(err);

      setError(`${i18n('Loyalty.CreateFailed')}: ${reason}`);
    } finally {
      setIsCreating(false);
    }
  };

  const backButton = useButton({ onClick: onBack, disabled: isCreating });
  const createButton = useButton({ onClick: create, disabled: isCreating });

  return (
    <>
      <Body>
        <Box as="label" display="block" style={{ marginTop: 18 }}>
          <Input
            dark
            ref={nameRef}
            placeholder={`${i18n('Loyalty.Name')} – Anna Lindqvist`}
            autoFocus
            autoComplete="off"
            autoCorrect="off"
            autoCapitalize="words"
            spellCheck={false}
          />
        </Box>

        <Box as="label" display="block" style={{ marginTop: 18 }}>
          <Input
            dark
            type="email"
            inputMode="email"
            ref={emailRef}
            placeholder={`${i18n('Loyalty.Email')} – anna@example.se`}
            autoComplete="off"
            autoCorrect="off"
            autoCapitalize="off"
            spellCheck={false}
          />
        </Box>

        <Box as="label" display="block" style={{ marginTop: 18 }}>
          <Input
            dark
            inputMode="numeric"
            pattern="[0-9]*"
            ref={personalIdRef}
            placeholder={`${i18n('Loyalty.PersonalId')} (${i18n(
              'Loyalty.Optional',
            )}) – 19850914xxxx`}
            maxLength={PERSONAL_ID_MAX_DIGITS}
            autoComplete="off"
            autoCorrect="off"
            autoCapitalize="off"
            spellCheck={false}
          />
        </Box>

        {error && <ErrorNote>{error}</ErrorNote>}
      </Body>

      <Footer>
        <CancelButton type="button" {...backButton}>
          {i18n('Loyalty.Back')}
        </CancelButton>
        <ConfirmButton type="button" {...createButton}>
          {isCreating ? '…' : i18n('Loyalty.Create')}
        </ConfirmButton>
      </Footer>
    </>
  );
};

export default CreateStep;
